/* iter55u — Extracted from UsersPanel.jsx.
 *
 * The subscription-tier chip + its "Manage subscription" dialog. Lets
 * an admin manually place a user on Free / Basic / Pro / Premier / Promo
 * (or clear to demo) without going through Stripe.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Crown, Save, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

// Public — other panels (e.g. BulkTierGrant) reuse these constants.
// Feb-2026 tier pricing: Scribe ($14/mo), Author ($39/mo), Publisher ($119/mo), Studio ($449/mo).
export const TIERS = [
  { id: '',          label: 'None (Demo)',    hint: 'Clear the subscription. User falls back to demo capabilities.' },
  { id: 'free',      label: 'Free',           hint: 'Permanent in-house test cohort using the saved Free permission matrix.' },
  { id: 'trial',     label: 'Trial',          hint: 'Time-limited public evaluation.' },
  { id: 'author',    label: 'Author',         hint: '$39/mo · Full AI toolkit — chapters, ISBNs, marketing, audiobook.' },
  { id: 'publisher', label: 'Publisher',      hint: '$119/mo · Bulk-draft + scripts + cross-book voice-lock.' },
  { id: 'studio',    label: 'Studio',         hint: '$449/mo · Every feature — all models, all tools, all exports.' },
  { id: 'promo',     label: 'Promo',          hint: "Full-featured trial. Writer's Launch campaign tier." },
];

export const TIER_STYLE = {
  '':        { chip: 'bg-slate-100 text-slate-700',     text: 'Demo' },
  free:      { chip: 'bg-teal-100 text-teal-800',       text: 'Free' },
  trial:     { chip: 'bg-cyan-100 text-cyan-800',       text: 'Trial' },
  author:    { chip: 'bg-emerald-100 text-emerald-800', text: 'Author' },
  publisher: { chip: 'bg-violet-100 text-violet-800',   text: 'Publisher' },
  studio:    { chip: 'bg-fuchsia-100 text-fuchsia-800', text: 'Studio' },
  promo:     { chip: 'bg-blue-100 text-blue-800',       text: 'Promo' },
  // Legacy tier IDs — display with asterisk so admins know they're migrated
  basic:     { chip: 'bg-amber-100 text-amber-800',     text: 'Author*' },
  pro:       { chip: 'bg-emerald-100 text-emerald-800', text: 'Author*' },
  pro_plus:  { chip: 'bg-emerald-100 text-emerald-800', text: 'Author*' },
  premier:   { chip: 'bg-violet-100 text-violet-800',   text: 'Publisher*' },
};

function fmtExp(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return ''; }
}

export function TierChip({ tier, expiresAt, onClick, disabled, testIdSuffix }) {
  const style = TIER_STYLE[tier || ''] || TIER_STYLE[''];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`admin-user-tier-chip-${testIdSuffix}`}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium ${style.chip} hover:ring-1 hover:ring-primary transition-shadow`}
      title={expiresAt ? `Expires ${fmtExp(expiresAt)}` : 'Click to change'}
    >
      <Crown className="h-2.5 w-2.5" />
      {style.text}
    </button>
  );
}

export default function TierManagerButton({ user, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState(user.subscription_tier || '');
  const [expiresAt, setExpiresAt] = useState(
    user.subscription_expires_at ? user.subscription_expires_at.slice(0, 10) : '',
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /* iter55f56 — Bonus token grant. Admin can hand tokens to in-house
   * teammates or comp a project without touching Stripe. Empty string
   * = no grant; positive integer = credit that many tokens on save. */
  const [tokenGrant, setTokenGrant] = useState('');

  useEffect(() => {
    if (open) {
      setTier(user.subscription_tier || '');
      setExpiresAt(user.subscription_expires_at ? user.subscription_expires_at.slice(0, 10) : '');
      setNote('');
      setTokenGrant('');
    }
  }, [open, user.subscription_tier, user.subscription_expires_at]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { tier: tier || null, note: note || null };
      if (tier && tier !== 'free' && expiresAt) {
        body.expires_at = new Date(`${expiresAt}T23:59:59Z`).toISOString();
      }
      await adminApi.setSubscription(user.user_id, body);
      // iter55f56 — Optional token grant on the same save. Fails soft:
      // the tier change is authoritative, tokens are best-effort.
      const grant = parseInt(tokenGrant, 10);
      if (Number.isFinite(grant) && grant > 0) {
        try {
          await adminApi.grantTokens?.(user.user_id, {
            amount: grant, note: note || 'In-house project grant',
          });
          toast.success(`+${grant.toLocaleString()} tokens granted to ${user.email || user.user_id}`);
        } catch (grantErr) {
          toast.warning('Tier saved, but token grant failed — try again from the Tokens panel.');
          console.warn('[TierManager] token grant failed:', grantErr);
        }
      }
      toast.success(tier
        ? `${user.email || user.user_id} → ${tier}`
        : `${user.email || user.user_id} → cleared`);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TierChip
        tier={user.subscription_tier}
        expiresAt={user.subscription_expires_at}
        onClick={() => setOpen(true)}
        testIdSuffix={user.user_id}
      />

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`admin-user-tier-dialog-${user.user_id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" />
              Manage subscription — {user.email || user.name || user.user_id}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Manually place this user on a tier without going through Stripe.
              Useful for comping, testing, or fixing webhook mishaps.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Tier</Label>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {TIERS.map((t) => (
                  <button
                    key={t.id || 'none'}
                    onClick={() => { setTier(t.id); if (t.id === 'free') setExpiresAt(''); }}
                    data-testid={`admin-user-tier-option-${t.id || 'none'}`}
                    data-active={tier === t.id ? '1' : '0'}
                    className={`rounded-md border p-2 text-left transition-colors ${
                      tier === t.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
                    }`}
                  >
                    <div className="text-[11.5px] font-semibold">{t.label}</div>
                    <div className="text-[10px] text-muted-foreground">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            {tier && tier !== 'free' && (
              <div>
                <Label className="text-[11px]">Expires (leave blank for +1 year)</Label>
                <Input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="h-8 text-[12px] mt-1"
                  data-testid={`admin-user-tier-expiry-${user.user_id}`}
                />
              </div>
            )}
            {tier === 'free' && (
              <div className="rounded-sm border border-teal-300 bg-teal-50 p-2 text-[10.5px] text-teal-900" data-testid={`admin-user-tier-free-internal-${user.user_id}`}>
                Internal test cohort · no expiration · never displayed in public pricing or signup.
              </div>
            )}
            <div>
              <Label className="text-[11px]">Note (audit log)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Comping David for QA testing"
                className="h-8 text-[12px] mt-1"
                data-testid={`admin-user-tier-note-${user.user_id}`}
              />
            </div>
            {/* iter55f56 — Optional token grant on the same save. Use
             * this to comp an in-house project or hand credits to a
             * teammate without pushing them through Stripe. */}
            <div>
              <Label className="text-[11px]">
                Bonus tokens to grant (in-house / project comp)
              </Label>
              <Input
                type="number"
                min="0"
                step="1000"
                value={tokenGrant}
                onChange={(e) => setTokenGrant(e.target.value)}
                placeholder="0"
                className="h-8 text-[12px] mt-1"
                data-testid={`admin-user-token-grant-${user.user_id}`}
              />
              <div className="mt-1 text-[10px] text-muted-foreground">
                Leave blank to skip. Positive integers only.
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`admin-user-tier-cancel-${user.user_id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submit(); }}
              disabled={busy}
              data-testid={`admin-user-tier-submit-${user.user_id}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
